import { alpha } from '@mui/material/styles';
export const generateTransparencyShades = (color) => ({
    8: alpha(color, 0.08),
    12: alpha(color, 0.12),
    16: alpha(color, 0.16),
    20: alpha(color, 0.2),
    24: alpha(color, 0.24),
    32: alpha(color, 0.32),
    48: alpha(color, 0.48),
    80: alpha(color, 0.8)
});
// Normalize color to ensure it has # prefix
const normalizeColor = (color) => {
    if (!color)
        return color;
    // If color doesn't start with #, add it
    return color.startsWith('#') ? color : `#${color}`;
};
// Generate color shades with opacity
export const generateColorShades = (baseColor, contrastText) => {
    const normalizedColor = normalizeColor(baseColor);
    const normalizedContrastText = normalizeColor(contrastText);
    return {
        20: alpha(normalizedColor, 0.2),
        40: alpha(normalizedColor, 0.4),
        60: alpha(normalizedColor, 0.6),
        80: alpha(normalizedColor, 0.8),
        100: normalizedColor,
        contrastText: normalizedContrastText
    };
};
// Helper function to determine if a color is light or dark
// Returns true if color is light (should use dark text), false if dark (should use light text)
const getLuminance = (hex) => {
    // Normalize color first
    const normalizedHex = normalizeColor(hex);
    // Remove # if present
    const color = normalizedHex.replace('#', '');
    // Convert to RGB
    const r = parseInt(color.substring(0, 2), 16) / 255;
    const g = parseInt(color.substring(2, 4), 16) / 255;
    const b = parseInt(color.substring(4, 6), 16) / 255;
    // Apply gamma correction
    const [rLinear, gLinear, bLinear] = [r, g, b].map((val) => {
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    // Calculate relative luminance
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};
// Determine contrast text color (black or white) based on background color
export const getContrastText = (backgroundColor) => {
    const normalizedColor = normalizeColor(backgroundColor);
    const luminance = getLuminance(normalizedColor);
    // If luminance is greater than 0.5, use dark text, otherwise use light text
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
};
// Generate lighter color by mixing with white
export const lightenColor = (color, amount) => {
    const normalizedColor = normalizeColor(color);
    const colorWithoutHash = normalizedColor.replace('#', '');
    // Convert to RGB
    const r = parseInt(colorWithoutHash.substring(0, 2), 16);
    const g = parseInt(colorWithoutHash.substring(2, 4), 16);
    const b = parseInt(colorWithoutHash.substring(4, 6), 16);
    // Lighten by mixing with white (255, 255, 255)
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
    // Convert back to hex
    const toHex = (n) => {
        const hex = n.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};
// Generate darker color by mixing with black
export const darkenColor = (color, amount) => {
    const normalizedColor = normalizeColor(color);
    const colorWithoutHash = normalizedColor.replace('#', '');
    // Convert to RGB
    const r = parseInt(colorWithoutHash.substring(0, 2), 16);
    const g = parseInt(colorWithoutHash.substring(2, 4), 16);
    const b = parseInt(colorWithoutHash.substring(4, 6), 16);
    // Darken by reducing RGB values (mixing with black)
    const newR = Math.max(0, Math.floor(r * (1 - amount)));
    const newG = Math.max(0, Math.floor(g * (1 - amount)));
    const newB = Math.max(0, Math.floor(b * (1 - amount)));
    // Convert back to hex
    const toHex = (n) => {
        const hex = n.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};
// Generate color variations (lighter, light, dark, darker) from a base color
export const generateColorVariations = (baseColor) => {
    const normalizedColor = normalizeColor(baseColor);
    return {
        lighter: lightenColor(normalizedColor, 0.7),
        light: lightenColor(normalizedColor, 0.4),
        main: normalizedColor,
        dark: darkenColor(normalizedColor, 0.3),
        darker: darkenColor(normalizedColor, 0.6) // Very dark
    };
};
// SETUP COLORS
const GREY = {
    0: '#FFFFFF',
    100: '#F9FAFB',
    200: '#F4F6F8',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
    700: '#454F5B',
    800: '#212B36',
    900: '#161C24',
    '500Transparent': generateTransparencyShades('#919EAB')
};
const PRIMARY = {
    lighter: '#CBF3FF',
    light: '#00B6ED',
    main: '#3192D0',
    dark: '#0B51A1',
    darker: '#00244D',
    contrastText: '#FFFFFF',
    gradient: {
        main: 'linear-gradient(135deg, #00244D 0%, #3192D0 100%)',
        bold: 'linear-gradient(135deg, #00B6ED 0%, #00244D 100%)'
    },
    shades: generateColorShades('#3192D0', '#FFFFFF')
};
const SECONDARY = {
    lighter: '#E5FFC7',
    light: '#ABFF4A',
    main: '#76B82A',
    dark: '#4D850C',
    darker: '#315805',
    contrastText: '#FFFFFF',
    gradient: {
        main: 'linear-gradient(135deg, #ABFF4A 0%, #4D850C 100%)',
        bold: 'linear-gradient(135deg, #303030 0%, #0A0A0A 100%)'
    },
    shades: generateColorShades('#76B82A', '#FFFFFF')
};
const INFO = {
    lighter: '#CAFDF5',
    light: '#61F3F3',
    main: '#00B8D9',
    dark: '#006C9C',
    darker: '#003768',
    contrastText: '#FFFFFF',
    gradient: {
        main: 'linear-gradient(135deg, #61F3F3 0%, #00B8D9 100%)',
        bold: 'linear-gradient(135deg, #00B8D9 0%, #006C9C 100%)'
    }
};
const SUCCESS = {
    lighter: '#E5FFC7',
    light: '#ABFF4A',
    main: '#76B82A',
    dark: '#4D850C',
    darker: '#315805',
    contrastText: '#FFFFFF',
    gradient: {
        main: 'linear-gradient(135deg, #77ED8B 0%, #22C55E 100%)',
        bold: 'linear-gradient(135deg, #22C55E 0%, #118D57 100%)'
    }
};
const WARNING = {
    lighter: '#FFF5CC',
    light: '#FFD666',
    main: '#FFAB00',
    dark: '#B76E00',
    darker: '#7A4100 ',
    contrastText: '#FFFFFF',
    gradient: {
        main: 'linear-gradient(135deg, #FFD666 0%, #FFAB00 100%)',
        bold: 'linear-gradient(135deg, #FFAB00 0%, #B76E00 100%)'
    }
};
const ERROR = {
    lighter: '#FFB0B0',
    light: '#FF5A59',
    main: '#E52322',
    dark: '#AD0605',
    darker: '#750909  ',
    contrastText: '#FFFFFF',
    gradient: {
        main: 'linear-gradient(135deg, #FFAC82 0%, #FF5630 100%)',
        bold: 'linear-gradient(135deg, #FF5630 0%, #B71D18 100%)'
    }
};
const GOLD = {
    lighter: '#FFF9C4',
    light: '#FFEA60',
    main: '#FFD700',
    dark: '#FFC107',
    darker: '#FF8F00',
    contrastText: '#000000'
};
// Color Shades from Design Tokens
const SKY_BLUE = generateColorShades('#00B6ED', '#000000'); // black/main
const OCEAN_BLUE = generateColorShades('#3192D0', '#FFFFFF'); // white/main
const DARK_BLUE = generateColorShades('#2D3077', '#FFFFFF'); // white/main
const MIDNIGHT_BLUE = generateColorShades('#00244D', '#FFFFFF'); // white/main
const RED = generateColorShades('#E52322', '#FFFFFF'); // white/main
const ORANGE = generateColorShades('#E94E1B', '#FFFFFF'); // white/main (E94E1B from design tokens)
const YELLOW = generateColorShades('#FFDD00', '#000000'); // black/main
const GREEN = generateColorShades('#76B82A', '#000000'); // black/main
const COMMON = {
    common: {
        black: '#000000',
        white: '#FFFFFF',
        blackTransparent: generateTransparencyShades('#000000'),
        whiteTransparent: generateTransparencyShades('#FFFFFF'),
        white92: alpha('#FFFFFF', 0.92),
        grey8: alpha('#919EAB', 0.08),
        grey32: alpha('#919EAB', 0.32),
        grey20: alpha('#919EAB', 0.2),
        grey24: alpha('#919EAB', 0.24),
        grey: {
            neutral: '#F5F5F8',
            main: '#9A9A98',
            dark: '#575756'
        },
        skyBlue: SKY_BLUE,
        oceanBlue: OCEAN_BLUE,
        darkBlue: DARK_BLUE,
        midnightBlue: MIDNIGHT_BLUE,
        red: RED,
        orange: ORANGE,
        yellow: YELLOW,
        green: GREEN
    },
    primary: PRIMARY,
    secondary: SECONDARY,
    info: INFO,
    success: SUCCESS,
    warning: WARNING,
    error: ERROR,
    grey: GREY,
    gold: GOLD,
    divider: GREY['500Transparent'][20],
    action: {
        active: '#575756',
        hover: GREY['500Transparent'][8],
        selected: GREY['500Transparent'][16],
        disabled: GREY['500Transparent'][80],
        disabledBackground: GREY['500Transparent'][24],
        focus: GREY['500Transparent'][24],
        hoverOpacity: 0.08,
        selectedOpacity: 0.08,
        disabledOpacity: 0.48,
        focusOpacity: 0.12,
        activatedOpacity: 0.12
    }
};
export function palette(mode) {
    const light = {
        ...COMMON,
        mode: 'light',
        text: {
            primary: GREY[900],
            secondary: GREY[700],
            disabled: GREY[500],
            tertiary: GREY[500]
        },
        background: {
            paper: COMMON.common.white,
            default: '#F9F9F9',
            neutral: '#F4F6F8'
        },
        action: {
            ...COMMON.action,
            active: '#575756'
        }
    };
    const dark = {
        ...COMMON,
        mode: 'dark',
        text: {
            primary: COMMON.common.white,
            secondary: GREY[500],
            disabled: GREY[600]
        },
        background: {
            paper: GREY[900],
            default: GREY[800],
            neutral: GREY['500Transparent'][12]
        },
        action: {
            ...COMMON.action,
            active: GREY[500]
        }
    };
    return mode === 'light' ? light : dark;
}
